import { type FC, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Chip,
  Divider,
  DropdownCard,
  DropdownContent,
  type IChipProps,
  type IDropdownItem,
  Scrollbar,
  Typography,
} from 'shared/ui'
import { TextField, makeTextFieldAutoFocus } from 'shared/ui/TextField'
import { NEW_INBOX_ID } from '../../../lib/constants'
import styles from './styles.module.scss'

export type IInboxDropdownContentProps = {
  selectedItems?: IDropdownItem[]
  items: IDropdownItem[]
  ChipComponent?: FC<IChipProps>
  onDelete?: (item: IDropdownItem) => void
  onSelect?: (items: IDropdownItem) => void
  onCloseMenu?: () => void
}

export const InboxDropdownContent = observer(
  ({
    items,
    selectedItems,
    onDelete,
    onSelect,
    onCloseMenu,
    ChipComponent = Chip,
  }: IInboxDropdownContentProps) => {
    const [search, setSearch] = useState('')

    const selectedIds = useMemo(
      () => new Set(selectedItems?.map((item) => item.id) ?? []),
      [selectedItems]
    )

    const newInboxItem = useMemo(() => items.find((item) => item.id === NEW_INBOX_ID), [items])

    const inboxItems = useMemo(() => items.filter((item) => item.id !== NEW_INBOX_ID), [items])

    const showItems = useMemo<IDropdownItem[]>(() => {
      const lowercasedSearch = search.toLowerCase()

      return inboxItems
        .filter((item) => {
          if (selectedIds.has(item.id)) return false
          if (!search) return true

          return item.label.toLowerCase().includes(lowercasedSearch)
        })
        .map((item) => ({ ...item, disabledClosedTooltip: true, iconL: 'inboxOpen' }))
    }, [inboxItems, search, selectedIds])

    return (
      <DropdownContent className={styles.dropdownContentWrap}>
        <Scrollbar autoHeight autoHeightMin={0}>
          <div className={styles.selectedInboxesWrap} aria-label='AddedInboxList'>
            {selectedItems
              ?.filter((item) => item.id !== NEW_INBOX_ID)
              .map((item) => (
                <ChipComponent
                  key={item.id}
                  item={item}
                  label={item.label}
                  iconType='div'
                  onDelete={() => onDelete?.(item)}
                />
              ))}
            <div style={{ width: '100%' }}>
              <TextField
                variant='integrated'
                size='medium'
                className={styles.search}
                value={search}
                onChange={(key) => setSearch(key)}
                InputProps={{
                  placeholder: 'Search',
                }}
                ariaLabel='InboxTextFieldSearchInput'
                mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
              />
            </div>
          </div>
        </Scrollbar>
        <Divider />
        <div aria-label='InboxListItems'>
          <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={300}>
            <div className={styles.searchTagsList}>
              {newInboxItem && (
                <>
                  <div className={styles.subList}>
                    <DropdownCard
                      key={newInboxItem.id}
                      item={newInboxItem}
                      onChange={(item) => {
                        onSelect?.(item)
                        setSearch('')
                        onCloseMenu?.()
                      }}
                    />
                  </div>
                  <Divider />
                </>
              )}
              {showItems.length > 0 && (
                <div className={styles.subList}>
                  {showItems.map((inbox) => (
                    <DropdownCard
                      key={inbox.id}
                      item={inbox}
                      onChange={(item) => {
                        onSelect?.(item)
                        setSearch('')
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            {!showItems.length && (
              <div className={styles.noResult}>
                <Typography
                  ariaLabel='NoResultsText'
                  variant='body-sm-regular'
                  color='var(--content-primary-tertiary)'
                >
                  No results found
                </Typography>
              </div>
            )}
          </Scrollbar>
        </div>
      </DropdownContent>
    )
  }
)
